import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ArrowLeft, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "./api";
import { PageHeader, Card, Field, Input, Textarea, Select, SaveBar } from "./components/Field";
import { ImageUpload } from "./components/ImageUpload";

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] tracking-[0.3em] uppercase font-bold px-2 py-1 ${
    status === "published" ? "bg-brand/30 text-brand-light border border-brand/50" : "bg-white/5 text-zinc-400 border border-white/10"
  }`}>{status}</span>
);

const PostsList = ({ types, posts, onSelect, onNew, activeType, setActiveType }) => {
  const filtered = posts.filter((p) => !activeType || p.type_key === activeType);
  return (
    <>
      <PageHeader
        overline="Manage"
        title="Posts & Custom Types"
        description="Blog posts, testimonials, clinics, gallery items, videos, FAQs, press mentions. Public rendering for these types comes in the next phase — for now, content is captured and stored cleanly."
        actions={
          <button onClick={onNew} className="btn-primary !py-2.5 !px-5 !text-sm" data-testid="posts-new-button">
            <Plus className="h-4 w-4" /> New Post
          </button>
        }
      />
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveType(null)}
          className={`text-xs tracking-[0.2em] uppercase font-bold px-3 py-2 border transition-colors ${
            !activeType ? "bg-brand text-white border-brand" : "border-white/15 text-zinc-300 hover:border-brand-light"
          }`}
        >
          All
        </button>
        {types.map((t) => (
          <button
            key={t.type_key}
            onClick={() => setActiveType(t.type_key)}
            className={`text-xs tracking-[0.2em] uppercase font-bold px-3 py-2 border transition-colors ${
              activeType === t.type_key ? "bg-brand text-white border-brand" : "border-white/15 text-zinc-300 hover:border-brand-light"
            }`}
            data-testid={`posts-filter-${t.type_key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid gap-3">
        {filtered.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            className="bento-card p-4 text-left hover:border-brand transition-colors"
            data-testid={`post-row-${p.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">
                    {types.find((t) => t.type_key === p.type_key)?.label || p.type_key}
                  </span>
                  <StatusBadge status={p.status} />
                </div>
                <div className="font-display uppercase text-lg tracking-tight truncate mt-1">{p.title}</div>
                <div className="text-xs text-zinc-500 truncate mt-0.5">/{p.slug}</div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-zinc-500 shrink-0" />
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-zinc-500 text-sm">No posts yet. Create one to get started.</div>}
      </div>
    </>
  );
};

const PostEditor = ({ types, post, onBack, onSaved }) => {
  const isNew = !post.id;
  const [data, setData] = useState({
    type_key: post.type_key || (types[0]?.type_key || "blog"),
    title: post.title || "",
    slug: post.slug || "",
    body: post.body || "",
    featured_image: post.featured_image || "",
    custom_fields: post.custom_fields || {},
    status: post.status || "draft",
  });
  const [original, setOriginal] = useState(JSON.stringify(data));
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(data) !== original;

  const update = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const updateField = (key, val) => setData((d) => ({ ...d, custom_fields: { ...d.custom_fields, [key]: val } }));

  const typeDef = types.find((t) => t.type_key === data.type_key);

  const onSave = async () => {
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        ({ data: saved } = await api.post("/cms/posts", data));
      } else {
        ({ data: saved } = await api.patch(`/cms/posts/${post.id}`, data));
      }
      toast.success("Saved.");
      setOriginal(JSON.stringify(data));
      onSaved(saved);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/cms/posts/${post.id}`);
      toast.success("Deleted.");
      onBack(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    }
  };

  return (
    <>
      <button onClick={() => onBack(false)} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 hover:text-white mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to posts
      </button>
      <PageHeader
        overline={typeDef?.label || data.type_key}
        title={isNew ? "New Post" : data.title || "Untitled"}
        actions={
          !isNew && (
            <button onClick={onDelete} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 hover:text-red-400 px-3 py-2">
              Delete
            </button>
          )
        }
      />

      <Card>
        <div className="grid gap-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Type">
              <Select
                value={data.type_key}
                onChange={(v) => update("type_key", v)}
                options={types.map((t) => ({ value: t.type_key, label: t.label }))}
                disabled={!isNew}
              />
            </Field>
            <Field label="Status">
              <Select
                value={data.status}
                onChange={(v) => update("status", v)}
                options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]}
              />
            </Field>
          </div>
          <Field label="Title"><Input value={data.title} onChange={(v) => update("title", v)} /></Field>
          <Field label="Slug (auto-generated if blank)" hint="URL-friendly ID for the post.">
            <Input value={data.slug} onChange={(v) => update("slug", v)} placeholder="my-post" />
          </Field>
          <ImageUpload label="Featured image" value={data.featured_image} onChange={(v) => update("featured_image", v)} />

          {(typeDef?.fields || []).map((f) => {
            const val = data.custom_fields[f.key] || "";
            if (f.type === "textarea" || f.type === "richtext") {
              return (
                <Field key={f.key} label={f.label}>
                  <Textarea rows={f.type === "richtext" ? 8 : 4} value={val} onChange={(v) => updateField(f.key, v)} />
                </Field>
              );
            }
            if (f.type === "tags") {
              const arr = Array.isArray(val) ? val : (val ? String(val).split(",").map((s) => s.trim()) : []);
              return (
                <Field key={f.key} label={f.label} hint="Comma-separated.">
                  <Input value={arr.join(", ")} onChange={(v) => updateField(f.key, v.split(",").map((s) => s.trim()).filter(Boolean))} />
                </Field>
              );
            }
            return (
              <Field key={f.key} label={f.label}>
                <Input type={f.type === "date" ? "date" : "text"} value={val} onChange={(v) => updateField(f.key, v)} />
              </Field>
            );
          })}
        </div>
      </Card>

      <SaveBar saving={saving} dirty={dirty || isNew} onSave={onSave} />
    </>
  );
};

export const Posts = () => {
  const [types, setTypes] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [t, p] = await Promise.all([
      api.get("/cms/post-types"),
      api.get("/cms/posts"),
    ]);
    setTypes(t.data);
    setPosts(p.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  if (editing) {
    return (
      <PostEditor
        types={types}
        post={editing}
        onBack={() => { setEditing(null); load(); }}
        onSaved={(saved) => setEditing(saved)}
      />
    );
  }

  return (
    <PostsList
      types={types}
      posts={posts}
      activeType={activeType}
      setActiveType={setActiveType}
      onNew={() => setEditing({ type_key: activeType || "blog", status: "draft" })}
      onSelect={(p) => setEditing(p)}
    />
  );
};
